// src/components/Navbar.jsx
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaArrowRight, FaChevronDown, FaBars } from "react-icons/fa6";
import { FaTimes } from "react-icons/fa";
import logo from "../components/home/logo.png";

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false); // Desktop dropdown
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Mobile menu
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false); // Mobile dropdown
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef(null);
  const location = useLocation();

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Products", path: "/products" },
    { name: "Machines", path: "/machines" },
    { name: "Units", path: "#" }, // dropdown
    { name: "About", path: "/about" },
    { name: "Blog", path: "/blog" },
  ];

  // Detect scroll for blur effect
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav
      className={`flex items-center justify-between py-6 px-8 sticky top-0 z-50 transition-all duration-300 backdrop-blur-md ${
        isScrolled ? "bg-white/70 shadow-md" : "bg-white/30"
      }`}
    >
      {/* Logo */}
      <Link to="/" className="flex justify-center items-center gap-4">
        <img
          src={logo}
          alt="Logo"
          className="h-8 w-auto sm:h-10 md:h-12 lg:h-16 object-contain"
        />
        <h1 className="max-md:hidden text-3xl text-[#d2271f] font-semibold">
          Twin Star Industries
        </h1>
      </Link>

      {/* Desktop Menu */}
      <ul className="hidden md:flex items-center space-x-6 font-medium">
        {navLinks.map((item) => {
          if (item.name === "Units") {
            return (
              <li key={item.name} ref={dropdownRef} className="relative group">
                <button
                  onClick={() => setDropdownOpen((prev) => !prev)}
                  className="flex items-center gap-1 transition-colors duration-300"
                >
                  {item.name}
                  <FaChevronDown
                    className={`transition-transform duration-300 ${
                      dropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>

                {/* Desktop Dropdown */}
                {dropdownOpen && (
                  <ul className="absolute top-full left-0 mt-2 w-44 bg-white shadow-lg rounded-md py-2 text-gray-700 border border-gray-200">
                    <li>
                      <Link
                        onClick={() =>
                          window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                        to="/units/main"
                        className="block px-4 py-2 hover:bg-gray-100 transition-all duration-300"
                      >
                        Industry Headquarters
                      </Link>
                    </li>
                    <li>
                      <Link
                        onClick={() =>
                          window.scrollTo({ top: 0, behavior: "smooth" })
                        }
                        to="/units/sub"
                        className="block px-4 py-2 hover:bg-gray-100 transition-all duration-300"
                      >
                        Unit 2
                      </Link>
                    </li>
                  </ul>
                )}
              </li>
            );
          } else {
            const isActive = location.pathname === item.path;
            return (
              <li
                key={item.name}
                className={`relative group cursor-pointer overflow-hidden h-6 ${
                  isActive ? "text-blue-900 font-bold" : ""
                }`}
              >
                <Link
                  to={item.path}
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                >
                  <span className="block transition-transform duration-300 transform group-hover:-translate-y-full">
                    {item.name}
                  </span>
                  <span className="absolute top-full left-0 w-full block transition-transform duration-300 transform group-hover:-translate-y-full">
                    {item.name}
                  </span>
                </Link>
              </li>
            );
          }
        })}
      </ul>

      {/* Desktop Contact Us Button */}
      <Link
        to="/contact"
        className="max-md:hidden relative overflow-hidden inline-flex items-center gap-2 px-6 py-3 sm:py-4 rounded-full border border-black bg-white text-black font-medium group transition-colors duration-300 hover:bg-blue-950"
      >
        <span className="relative z-10 flex items-center gap-2">
          <span className="relative block overflow-hidden h-6">
            <span className="flex justify-center items-center gap-3 transition-transform duration-300 transform group-hover:-translate-y-full">
              Contact Us
              <FaArrowRight size={18} color="black" />
            </span>
            <span className="absolute top-full flex justify-center items-center gap-3 text-white left-0 w-full transition-transform duration-300 transform group-hover:-translate-y-full">
              Contact Us
              <FaArrowRight size={18} color="white" />
            </span>
          </span>
        </span>
      </Link>

      {/* Mobile Menu Icon */}
      <div className="md:hidden z-50">
        {mobileMenuOpen ? (
          <FaTimes
            size={24}
            onClick={() => setMobileMenuOpen(false)}
            className="cursor-pointer"
          />
        ) : (
          <FaBars
            size={24}
            onClick={() => setMobileMenuOpen(true)}
            className="cursor-pointer"
          />
        )}
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="absolute top-16 left-0 w-full bg-white shadow-lg md:hidden z-40">
          <ul className="flex flex-col items-center gap-4 py-6 text-gray-800 font-medium">
            {navLinks.map((item) => {
              if (item.name === "Units") {
                return (
                  <li key={item.name} className="w-full text-center">
                    <div
                      onClick={() =>
                        setMobileDropdownOpen((prev) => !prev)
                      }
                      className="flex justify-center items-center gap-1 w-full py-2 cursor-pointer"
                    >
                      {item.name}
                      <FaChevronDown
                        className={`transition-transform duration-300 ${
                          mobileDropdownOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>
                    {mobileDropdownOpen && (
                      <div className="flex flex-col items-center mt-2">
                        <Link
                          to="/units/main"
                          className="block px-4 py-2 hover:bg-gray-100 w-full text-center"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileDropdownOpen(false);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Industry Headquarters
                        </Link>
                        <Link
                          to="/units/sub"
                          className="block px-4 py-2 hover:bg-gray-100 w-full text-center"
                          onClick={() => {
                            setMobileMenuOpen(false);
                            setMobileDropdownOpen(false);
                            window.scrollTo({ top: 0, behavior: "smooth" });
                          }}
                        >
                          Unit 2
                        </Link>
                      </div>
                    )}
                  </li>
                );
              } else {
                return (
                  <li
                    key={item.name}
                    className={`w-full text-center ${
                      location.pathname === item.path
                        ? "text-green-800 font-semibold"
                        : ""
                    }`}
                  >
                    <Link
                      to={item.path}
                      onClick={() => {
                        setMobileMenuOpen(false);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                    >
                      {item.name}
                    </Link>
                  </li>
                );
              }
            })}

            {/* Mobile Contact Us Button */}
            <Link
              to="/contact"
              className="relative overflow-hidden inline-flex items-center gap-2 px-6 py-3 sm:py-4 rounded-full border border-black hover:bg-blue-950 text-black font-medium group transition-colors duration-300 bg-white hover:text-black"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="relative z-10 flex items-center gap-2">
                <span className="relative block overflow-hidden h-6">
                  <span className="block transition-transform duration-300 transform group-hover:-translate-y-full">
                    Contact Us
                  </span>
                  <span className="absolute top-full left-0 w-full block transition-transform duration-300 transform group-hover:-translate-y-full">
                    Contact Us
                  </span>
                </span>
                <FaArrowRight size={18} />
              </span>
            </Link>
          </ul>
        </div>
      )}
    </nav>
  );
}
